import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Space, Table, message } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { useApi } from "../../helpers/useApi";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { SupplierReturn } from "../../api-models";

export default function SupplierReturnDetailPage() {
  const { id } = useParams();
  const { user } = useUser();
  const { request, loading } = useApi();
  const [sr, setSr] = useState<SupplierReturn | null>(null);
  const canWrite = hasAnyGrant(user, "SupplierReturns.Write");

  async function load() {
    setSr(await request<SupplierReturn>({ url: `/supplier-returns/${id}` }));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function act(action: string) {
    await request({ method: "POST", url: `/supplier-returns/${id}/${action}` });
    message.success("Done.");
    load();
  }

  if (!sr) return null;

  return (
    <div>
      <PageHeader
        title={sr.docNo}
        subtitle={`Supplier: ${sr.supplier?.name ?? sr.supplierId}`}
        extra={
          <Space wrap>
            <span data-tour="doc-status">
              <DocumentStatusTag status={sr.status} />
            </span>
            {sr.status === "DRAFT" && canWrite && (
              <Button type="primary" onClick={() => act("submit")} loading={loading}>
                Submit for approval
              </Button>
            )}
            {(sr.status === "DRAFT" || sr.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={() => act("cancel")}>
                Cancel
              </Button>
            )}
            {sr.status === "APPROVED" && canWrite && <Button onClick={() => act("dispatch")}>Dispatch to supplier</Button>}
          </Space>
        }
      />

      {sr.status === "APPROVED" && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Ready to dispatch"
          description="Dispatch posts Return Holding → the supplier's external location. The goods leave our ledger there; there is no receipt event on our side."
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Supplier">{sr.supplier?.name}</Descriptions.Item>
          <Descriptions.Item label="Reason">{sr.reason || "—"}</Descriptions.Item>
          <Descriptions.Item label="Dispatched">{sr.dispatchedAt ? new Date(sr.dispatchedAt).toLocaleString() : "—"}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Lines" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          dataSource={sr.lines ?? []}
          pagination={false}
          columns={[
            { title: "Code", render: (_, r) => r.item?.code },
            { title: "Item", render: (_, r) => r.item?.name },
            { title: "Qty", dataIndex: "qty" },
            { title: "Claimed from disposition", dataIndex: "sourceDispositionId" },
            { title: "Posted txn", render: (_, r) => r.dispatchTxnId ?? "—" },
          ]}
        />
      </Card>

      <ApprovalTimeline docType="SUPPLIER_RETURN" docId={Number(id)} refreshKey={sr?.status} onActed={load} />
    </div>
  );
}
